import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Button, Form, ListGroup, Modal } from "react-bootstrap";
import {Note} from "@models/note.model.ts";
import {NotebookService} from "@services/notebook/notebookService.ts";
import { useSnapshot } from 'valtio';
import { notebookStore } from '../../../notebookStore.ts'
import ConfirmActionModal from "../../../../contentCatalog/components/ConfirmActionModal.tsx";
import TreeNavigator, { type TreeNode } from "@components/TreeNavigator/TreeNavigator.tsx";
import { cancelPendingSaveFor } from "../../note-editor/NoteEditor.tsx";
import { formatNoteDate } from "@/shared/utils/noteDate";


// ── Hover ellipsis dropdown for each note row ───────────────────

interface NoteActionsProps {
    node: TreeNode;
    onAddChild: (node: TreeNode) => void;
    onDelete: (node: TreeNode) => void;
    onSaveAsTemplate: (node: TreeNode) => void;
    onUseTemplate: (node: TreeNode) => void;
}

const NoteActions = ({ node, onAddChild, onDelete, onSaveAsTemplate, onUseTemplate }: NoteActionsProps) => {
    const [open, setOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
    const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 150); };

    return (
        <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="notes-kebab ms-1"
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => { cancelClose(); setOpen(true); }}
                    onMouseLeave={scheduleClose}
                >
                    <i className="bi bi-three-dots-vertical" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="radix-context-menu-content"
                    align="end"
                    sideOffset={4}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                >
                    <DropdownMenu.Item
                        className="radix-context-menu-item"
                        onSelect={() => onAddChild(node)}
                    >
                        <i className="bi bi-plus-lg me-2" />Add Note
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="radix-context-menu-sep" />
                    <DropdownMenu.Item
                        className="radix-context-menu-item radix-context-menu-item--danger"
                        onSelect={() => onDelete(node)}
                    >
                        <i className="bi bi-trash me-2" />Delete Note
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="radix-context-menu-sep" />
                    <DropdownMenu.Item
                        className="radix-context-menu-item"
                        onSelect={() => onSaveAsTemplate(node)}
                    >
                        <i className="bi bi-file-earmark-arrow-up me-2" />Save as Template
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                        className="radix-context-menu-item"
                        onSelect={() => onUseTemplate(node)}
                    >
                        <i className="bi bi-file-earmark-arrow-down me-2" />Use Template
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};


// ── Helpers to convert Note[] <-> TreeNode[] ────────────────────

function notesToTreeNodes(notes: Note[]): TreeNode[] {
    return notes.map(n => ({
        id: String(n.id),
        label: stripHtml(n.title),
        type: "note",
        // The date and which campaign the note is about (MPN-1640). Without them the
        // list cannot be read as a timeline, and three campaigns' notes under one
        // engagement arrive interleaved with nothing to tell them apart.
        meta: <NoteMeta note={n} />,
        data: n,
        children: n.children ? notesToTreeNodes(n.children) : undefined,
    }));
}

function NoteMeta({ note }: { note: Note }) {
    const date = formatNoteDate(note.create_date);
    // "Engagement-wide" rather than nothing: an untagged note is a deliberate scope, and
    // a blank space next to tagged siblings reads as one nobody got round to filing.
    const scope = note.launch_name ?? "Engagement-wide";
    return (
        <span className="note-meta">
            {date && <span className="note-meta__date">{date}</span>}
            <span className={`note-meta__scope${note.launch_name ? " note-meta__scope--campaign" : ""}`}>
                {scope}
            </span>
        </span>
    );
}

function stripHtml(html: string) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
}

// ── NoteList component ──────────────────────────────────────────

interface NoteListProps {
    notebookId: number
}

export const NoteList = (props: NoteListProps) => {

    const {
        notebookId
    } = props;

    const [notes, setNotes] = useState<Note[]>([]);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [deleteTarget, setDeleteTarget] = useState<Note | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [templateTarget, setTemplateTarget] = useState<Note | null>(null);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [useTemplateTarget, setUseTemplateTarget] = useState<Note | null>(null);
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
    const [newFromTemplate, setNewFromTemplate] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const notebookService = new NotebookService();
    const snap = useSnapshot(notebookStore);

    useEffect(() => {
        const loadTree = async () => {
            try {
                const rootNotes = await notebookService.getNotes(notebookId, null);

                // Recursively fetch children at all levels
                const loadChildren = async (notes: any[]) => {
                    for (const note of notes) {
                        const children = await notebookService.getNotes(notebookId, note.id);
                        note.children = children;
                        if (children.length > 0) {
                            await loadChildren(children);
                        }
                    }
                };

                await loadChildren(rootNotes);
                setNotes(rootNotes);

                // If a note is already selected (e.g. popout), expand its ancestors.
                const selectedId = notebookStore.selectedNote?.id;
                if (selectedId && selectedId !== -1) {
                    const ancestors = new Set<string>();
                    const findAncestors = (nodes: Note[], path: string[]): boolean => {
                        for (const n of nodes) {
                            if (n.id === selectedId) {
                                path.forEach(id => ancestors.add(id));
                                return true;
                            }
                            if (n.children && n.children.length > 0) {
                                if (findAncestors(n.children, [...path, String(n.id)])) return true;
                            }
                        }
                        return false;
                    };
                    findAncestors(rootNotes, []);
                    setExpandedIds(ancestors);
                } else {
                    setExpandedIds(new Set());
                }
            } catch (error) {
                console.error("Get Notes:", error);
                throw error;
            }
        }
        loadTree().then()
    }, []);

    // When the editor auto-creates a note, add it to the tree.
    useEffect(() => {
        if (!snap.pendingNote) return;
        setNotes(prev => [snap.pendingNote as Note, ...prev]);
        notebookStore.pendingNote = null;
    }, [snap.pendingNote]);

    // When the header "+ Use Template" button is clicked, open the template picker.
    useEffect(() => {
        if (!snap.showNewFromTemplate) return;
        notebookStore.showNewFromTemplate = false;
        setNewFromTemplate(true);
        setSelectedTemplateId(null);
        setShowTemplatePicker(true);
        notebookService.getTemplates(notebookId).then(setTemplates).catch(() => setTemplates([]));
    }, [snap.showNewFromTemplate]);

    // In the Editor, when the Title is changed, we update the matching item here.
    // Must recurse into children — the note may be nested at any depth.
    useEffect(() => {
        if (!snap.selectedNote) return;

        const updateTitle = (nodes: Note[]): Note[] =>
            nodes.map(n => {
                if (n.id === snap.selectedNote.id) {
                    return { ...n, title: snap.selectedNote.title };
                }
                if (n.children && n.children.length > 0) {
                    return { ...n, children: updateTitle(n.children) };
                }
                return n;
            });

        setNotes(prev => updateTitle(prev));
    }, [snap.selectedNote?.id, snap.selectedNote?.title]);

    // Convert notes to TreeNode format for TreeNavigator
    const treeNodes = useMemo(() => notesToTreeNodes(notes), [notes]);

    // ── Tree helpers ────────────────────────────────────────────

    const insertChildren = (nodes: Note[], parentId: number, children: Note[]): Note[] => {
        return nodes.map(node => {
            if (node.id === parentId) {
                return { ...node, children };
            }
            if (node.children) {
                return { ...node, children: insertChildren(node.children, parentId, children) };
            }
            return node;
        });
    };

    const removeNode = (nodes: any, nodeId: any) => {
        return nodes
            .map((node: any) => {
                if (node.id === nodeId) return null;
                if (node.children) {
                    return { ...node, children: removeNode(node.children, nodeId) };
                }
                return node;
            })
            .filter(Boolean);
    }

    const insertNode = (nodes: any, parentId: any, node: any) => {
        return nodes.map((n: any) => {
            if (n.id === parentId) {
                const children = n.children ? [...n.children] : [];
                children.push(node);
                return { ...n, children };
            }
            if (n.children) {
                return { ...n, children: insertNode(n.children, parentId, node) };
            }
            return n;
        });
    }

    const findNode = (nodes: Note[], id: number): Note | null => {
        for (const node of nodes) {
            if (node.id === id) return node;
            if (node.children) {
                const found = findNode(node.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    // ── TreeNavigator callbacks ─────────────────────────────────

    const handleSelect = useCallback((treeNode: TreeNode) => {
        notebookStore.selectedNote = treeNode.data as Note;
    }, []);

    const handleToggle = useCallback(async (nodeId: string) => {
        const numericId = Number(nodeId);
        const node = findNode(notes, numericId);

        // Fetch children from API if not yet loaded
        if (node && (!node.children || node.children.length === 0)) {
            const children = await notebookService.getNotes(notebookId, numericId);
            if (children.length > 0) {
                setNotes(prev => insertChildren(prev, numericId, children));
            }
        }

        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) next.delete(nodeId);
            else next.add(nodeId);
            return next;
        });
    }, [notes, notebookId]);

    const handleNodeDrop = useCallback((dragNodeId: string, dropNodeId: string | null) => {
        const dragId = Number(dragNodeId);

        const dragNode = findNode(notes, dragId);
        if (!dragNode) return;

        let updated = removeNode(notes, dragId);

        if (dropNodeId === null) {
            // Dropped on root — move to top level
            updated = [...updated, dragNode];
        } else {
            const dropId = Number(dropNodeId);
            updated = insertNode(updated, dropId, dragNode);
        }

        setNotes(updated);

        if (dropNodeId !== null) {
            setExpandedIds(prev => new Set([...prev, dropNodeId]));
        }

        const note = new Note();
        note.id = dragId;
        note.parent_id = dropNodeId !== null ? Number(dropNodeId) : null;
        notebookService.moveNote(notebookId, note).then();
    }, [notes, notebookId]);

    // ── Note actions ────────────────────────────────────────────

    const handleNewNote = async () => {
        const newNote = await notebookService.createNote( notebookId );
        setNotes(prev => [newNote, ...prev]);
        notebookStore.selectedNote = newNote;
    }

    const handleAddChildNote = useCallback(async (treeNode: TreeNode) => {
        const parentNote = treeNode.data as Note;
        const newNote = await notebookService.createNote(notebookId, parentNote.id);
        setNotes(prev => {
            const existing = findNode(prev, parentNote.id);
            const currentChildren = existing?.children || [];
            return insertChildren(prev, parentNote.id, [...currentChildren, newNote]);
        });
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.add(String(parentNote.id));
            return next;
        });
        notebookStore.selectedNote = newNote;
    }, [notebookId]);

    const handleDeleteNote = useCallback((treeNode: TreeNode) => {
        setDeleteTarget(treeNode.data as Note);
    }, []);

    const confirmDeleteNote = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            cancelPendingSaveFor(deleteTarget.id);
            await notebookService.deleteNote(notebookId, deleteTarget.id);
            setNotes(prev => removeNode(prev, deleteTarget.id));
            if (notebookStore.selectedNote?.id === deleteTarget.id) {
                notebookStore.selectedNote = new Note();
            }
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const [saveTemplateName, setSaveTemplateName] = useState("");

    const handleSaveAsTemplate = useCallback(async (node: TreeNode) => {
        setTemplateTarget(node.data as Note);
        setSaveTemplateName("");
        try {
            const results = await notebookService.getTemplates(notebookId);
            setTemplates(results);
        } catch (error) {
            console.error("Failed to load templates:", error);
            setTemplates([]);
        }
    }, [notebookId]);

    const confirmSaveAsTemplate = async (templateName: string) => {
        if (!templateTarget) return;
        setIsSavingTemplate(true);
        try {
            const source = await notebookService.getNoteDetails(notebookId, templateTarget.id);
            const templates = await notebookService.getTemplates(notebookId);
            const existing = templates.find((t: any) => t.title === templateName);

            if (existing) {
                const note = new Note();
                note.id = existing.id;
                note.title = templateName;
                note.content = source.content;
                await notebookService.updateNote(notebookId, note);
            } else {
                await notebookService.createNote(notebookId, null, templateName, source.content, 'template');
            }
        } finally {
            setIsSavingTemplate(false);
            setTemplateTarget(null);
        }
    };

    const handleUseTemplate = useCallback(async (node: TreeNode) => {
        setUseTemplateTarget(node.data as Note);
        setNewFromTemplate(false);
        setSelectedTemplateId(null);
        setShowTemplatePicker(true);
        try {
            const results = await notebookService.getTemplates(notebookId);
            setTemplates(results);
        } catch (error) {
            console.error("Failed to load templates:", error);
            setTemplates([]);
        }
    }, [notebookId]);

    const closeTemplatePicker = () => {
        setShowTemplatePicker(false);
        setUseTemplateTarget(null);
        setNewFromTemplate(false);
        setSelectedTemplateId(null);
    };

    const confirmUseTemplate = async () => {
        if (!selectedTemplateId) return;
        setIsApplyingTemplate(true);
        try {
            const template = await notebookService.getNoteDetails(notebookId, selectedTemplateId);

            if (newFromTemplate) {
                const newNote = await notebookService.createNote(notebookId, null, template.title, template.content);
                setNotes(prev => [newNote, ...prev]);
                notebookStore.selectedNote = newNote;
            } else if (useTemplateTarget) {
                const note = new Note();
                note.id = useTemplateTarget.id;
                note.title = template.title;
                note.content = template.content;
                const updated = await notebookService.updateNote(notebookId, note) as unknown as Note;
                if (notebookStore.selectedNote?.id === useTemplateTarget.id) {
                    notebookStore.selectedNote = updated;
                    notebookStore.noteRefreshKey++;
                }
            }
        } finally {
            setIsApplyingTemplate(false);
            closeTemplatePicker();
        }
    };

    const renderActions = useCallback((node: TreeNode) => (
        <NoteActions
            node={node}
            onAddChild={handleAddChildNote}
            onDelete={handleDeleteNote}
            onSaveAsTemplate={handleSaveAsTemplate}
            onUseTemplate={handleUseTemplate}
        />
    ), [handleAddChildNote, handleDeleteNote, handleSaveAsTemplate, handleUseTemplate]);

    return (
        <>
            <div className="notes-rail">
                <div className="notes-rail__head">
                    Notes
                    <span className="notes-rail__count">{notes.length}</span>
                    <button
                        type="button"
                        className="notes-rail__new"
                        title="New note"
                        onClick={handleNewNote}
                    >
                        <i className="bi bi-plus-lg" />
                        New
                    </button>
                </div>

                <div className="notes-rail__body">
                    {notes.length === 0 ? (
                        <div className="notes-rail__empty">
                            No notes yet — start typing on the right, or add one.
                        </div>
                    ) : (
                        <TreeNavigator
                            nodes={treeNodes}
                            selectedId={snap.selectedNote?.id != null ? String(snap.selectedNote.id) : null}
                            onSelect={handleSelect}
                            expandedIds={expandedIds}
                            onToggle={handleToggle}
                            renderActions={renderActions}
                            draggable
                            onNodeDrop={handleNodeDrop}
                        />
                    )}
                </div>
            </div>

            <ConfirmActionModal
                show={deleteTarget !== null}
                title="Delete Note"
                message={deleteTarget ? `Delete "${stripHtml(deleteTarget.title)}"? This cannot be undone.` : ""}
                confirmLabel="Delete"
                onConfirm={confirmDeleteNote}
                onClose={() => setDeleteTarget(null)}
                isLoading={isDeleting}
            />

            <Modal
                centered
                show={templateTarget !== null}
                onHide={isSavingTemplate ? undefined : () => setTemplateTarget(null)}
                backdrop={isSavingTemplate ? "static" : true}
                keyboard={!isSavingTemplate}
            >
                <Modal.Header closeButton={!isSavingTemplate}>
                    <Modal.Title>Save as Template</Modal.Title>
                </Modal.Header>
                <form onSubmit={(e) => { e.preventDefault(); if (saveTemplateName.trim()) confirmSaveAsTemplate(saveTemplateName.trim()); }}>
                    <Modal.Body>
                        <Form.Control
                            autoFocus
                            value={saveTemplateName}
                            onChange={(e) => setSaveTemplateName(e.target.value)}
                            placeholder="Enter a name"
                            disabled={isSavingTemplate}
                        />
                        {templates.length > 0 && (
                            <>
                                <div className="text-muted small mt-3 mb-2">Existing templates</div>
                                <ListGroup>
                                    {templates.map((t: any) => (
                                        <ListGroup.Item
                                            key={t.id}
                                            action
                                            type="button"
                                            active={saveTemplateName === t.title}
                                            onClick={() => setSaveTemplateName(t.title)}
                                            disabled={isSavingTemplate}
                                        >
                                            {t.title}
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-info" onClick={() => setTemplateTarget(null)} disabled={isSavingTemplate}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={!saveTemplateName.trim() || isSavingTemplate}>
                            {isSavingTemplate ? "Saving..." : "Save"}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal
                centered
                show={showTemplatePicker}
                onHide={isApplyingTemplate ? undefined : closeTemplatePicker}
                backdrop={isApplyingTemplate ? "static" : true}
                keyboard={!isApplyingTemplate}
            >
                <Modal.Header closeButton={!isApplyingTemplate}>
                    <Modal.Title>Use Template</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {templates.length === 0 ? (
                        <p className="text-muted mb-0">No templates available.</p>
                    ) : (
                        <ListGroup>
                            {templates.map((t: any) => (
                                <ListGroup.Item
                                    key={t.id}
                                    action
                                    active={selectedTemplateId === t.id}
                                    onClick={() => setSelectedTemplateId(t.id)}
                                    disabled={isApplyingTemplate}
                                >
                                    {t.title}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-info" onClick={closeTemplatePicker} disabled={isApplyingTemplate}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmUseTemplate} disabled={!selectedTemplateId || isApplyingTemplate}>
                        {isApplyingTemplate ? "Applying..." : "Apply"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
